import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { FileSpecificationService } from 'src/app/services/file-specifications/file-specification.service';
import { FileSpecification } from 'src/app/models/file-specifications/file-specification';
import { FileSpecificationStillsSettings } from 'src/app/models/file-specifications/file-specification-settings';
import { StillsDimensions } from 'src/app/models/file-specifications/stills-dimensions';
import { StillsFormatSettings } from 'src/app/models/file-specifications/stills-format-settings';
import { StillsSubjectCropping } from 'src/app/models/file-specifications/stills-subject-cropping';
import { WebPCompression } from 'src/app/models/file-specifications/webp-compression';
import { ColorSpace, ALL_COLOR_SPACES } from 'src/app/models/file-specifications/color-space';
import {
  FileSpecificationWriteReason,
  FileSpecificationWriteReasonKind,
} from 'src/app/models/file-specifications/file-specification-write-reason';

export interface StillsFileSpecificationUpdateDialogData {
  organizationId: string;
  current: FileSpecification;
}

export type StillsFormatKind = StillsFormatSettings['kind'];
export type StillsDimensionsMode = StillsDimensions['mode'];
export type StillsSubjectCroppingMode = StillsSubjectCropping['mode'];
export type WebPCompressionMode = WebPCompression['mode'];

type UpdateReasonKind = Exclude<FileSpecificationWriteReasonKind, 'initialCreate' | 'deleted'>;

const SELECTABLE_REASONS: readonly UpdateReasonKind[] = ['preferenceUpdate', 'correction', 'other'];

const REASON_LABELS: Record<UpdateReasonKind, string> = {
  preferenceUpdate: 'Preference update',
  correction: 'Correction',
  other: 'Other',
};

const ALL_STILLS_FORMAT_KINDS: readonly StillsFormatKind[] = [
  'jpg',
  'png',
  'tiff',
  'webp',
  'eps',
] as const;

const STILLS_FORMAT_LABELS: Record<StillsFormatKind, string> = {
  jpg: 'JPG',
  png: 'PNG',
  tiff: 'TIFF',
  webp: 'WebP',
  eps: 'EPS',
};

const ALL_DIMENSIONS_MODES: readonly StillsDimensionsMode[] = [
  'explicit',
  'fitWidth',
  'fitHeight',
  'longEdgeCap',
  'longEdgeBox',
] as const;

const DIMENSIONS_MODE_LABELS: Record<StillsDimensionsMode, string> = {
  fitWidth: 'Fit width',
  fitHeight: 'Fit height',
  explicit: 'Explicit (W × H)',
  longEdgeCap: 'Long edge cap',
  longEdgeBox: 'Long-edge box',
};

const ALL_SUBJECT_CROPPING_MODES: readonly StillsSubjectCroppingMode[] = ['off', 'on'] as const;

const SUBJECT_CROPPING_MODE_LABELS: Record<StillsSubjectCroppingMode, string> = {
  off: 'Off',
  on: 'On',
};

const ALL_WEBP_COMPRESSION_MODES: readonly WebPCompressionMode[] = ['lossy', 'lossless'] as const;

const WEBP_COMPRESSION_MODE_LABELS: Record<WebPCompressionMode, string> = {
  lossy: 'Lossy',
  lossless: 'Lossless',
};

const ALL_BIT_DEPTHS: readonly number[] = [8, 16] as const;

const integerValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value;
  if (value === null || value === undefined || value === '') return null;
  return Number.isInteger(Number(value)) ? null : { integer: true };
};

// Legacy shape `{mode: 'longEdge', longEdge, shortEdge}` predates the ADR-009 P2
// split into `longEdgeCap` / `longEdgeBox`. No live backend has produced it yet,
// but tests + hand-crafted fixtures may still carry it; keep it tolerant so a
// future backend build-out doesn't break the FE.
interface LegacyLongEdgeShape {
  mode: 'longEdge';
  longEdge: number;
  shortEdge: number | null;
}

const asLegacyLongEdge = (dims: StillsDimensions): LegacyLongEdgeShape | null => {
  const raw = dims as unknown as { mode: string };
  if (raw.mode !== 'longEdge') return null;
  return dims as unknown as LegacyLongEdgeShape;
};

interface SeededLongEdgeShape {
  longEdge: number;
  shortEdge: number;
}

const seedLongEdgeFromLegacy = (dims: StillsDimensions): SeededLongEdgeShape => {
  if (dims.mode === 'longEdgeCap') return { longEdge: dims.longEdge, shortEdge: 1000 };
  if (dims.mode === 'longEdgeBox') return { longEdge: dims.longEdge, shortEdge: dims.shortEdge };
  const legacy = asLegacyLongEdge(dims);
  if (legacy !== null) {
    const short = legacy.shortEdge ?? 0;
    return { longEdge: legacy.longEdge, shortEdge: short > 0 ? short : 1000 };
  }
  return { longEdge: 2000, shortEdge: 1000 };
};

const resolveSeededMode = (dims: StillsDimensions): StillsDimensionsMode => {
  const legacy = asLegacyLongEdge(dims);
  if (legacy !== null) {
    const short = legacy.shortEdge ?? 0;
    return short > 0 ? 'longEdgeBox' : 'longEdgeCap';
  }
  return dims.mode;
};

@Component({
  selector: 'app-stills-file-specification-update-dialog',
  templateUrl: './stills-file-specification-update-dialog.component.html',
  styleUrls: ['./stills-file-specification-update-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class StillsFileSpecificationUpdateDialogComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly selectableReasons = SELECTABLE_REASONS;
  readonly reasonLabel = (k: UpdateReasonKind): string => REASON_LABELS[k];

  readonly formatKinds = ALL_STILLS_FORMAT_KINDS;
  readonly dimensionsModes = ALL_DIMENSIONS_MODES;
  readonly subjectCroppingModes = ALL_SUBJECT_CROPPING_MODES;
  readonly webpCompressionModes = ALL_WEBP_COMPRESSION_MODES;
  readonly colorSpaces = ALL_COLOR_SPACES;
  readonly bitDepths = ALL_BIT_DEPTHS;
  readonly formatLabel = (k: StillsFormatKind): string => STILLS_FORMAT_LABELS[k];
  readonly dimensionsModeLabel = (m: StillsDimensionsMode): string => DIMENSIONS_MODE_LABELS[m];
  readonly subjectCroppingModeLabel = (m: StillsSubjectCroppingMode): string =>
    SUBJECT_CROPPING_MODE_LABELS[m];
  readonly webpCompressionModeLabel = (m: WebPCompressionMode): string =>
    WEBP_COMPRESSION_MODE_LABELS[m];

  readonly nameControl: FormControl<string>;
  readonly formatControl: FormControl<StillsFormatKind>;
  readonly dimensionsModeControl: FormControl<StillsDimensionsMode>;
  readonly widthControl: FormControl<number>;
  readonly heightControl: FormControl<number>;
  readonly longEdgeControl: FormControl<number>;
  readonly shortEdgeControl: FormControl<number>;
  readonly dpiControl: FormControl<number>;
  readonly jpgQualityControl: FormControl<number>;
  readonly pngBitDepthControl: FormControl<number>;
  readonly tiffBitDepthControl: FormControl<number>;
  readonly webpCompressionModeControl: FormControl<WebPCompressionMode>;
  readonly webpQualityControl: FormControl<number>;
  readonly colorSpaceControl: FormControl<ColorSpace>;
  readonly subjectCroppingModeControl: FormControl<StillsSubjectCroppingMode>;
  readonly whitespacePercentControl: FormControl<number>;
  readonly backgroundColourControl: FormControl<string>;

  readonly reasonControl = new FormControl<UpdateReasonKind | null>(null, {
    validators: [Validators.required],
  });
  readonly detailControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(3)],
  });

  submitting = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: StillsFileSpecificationUpdateDialogData,
    private readonly dialogRef: MatDialogRef<
      StillsFileSpecificationUpdateDialogComponent,
      FileSpecification | undefined
    >,
    private readonly service: FileSpecificationService,
  ) {
    const settings = data.current.value.settings as FileSpecificationStillsSettings;
    const format = settings.encoding.format;
    const dims = settings.dimensions;
    const crop = settings.subjectCropping;

    this.nameControl = new FormControl<string>(data.current.name, {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(1)],
    });

    this.formatControl = new FormControl<StillsFormatKind>(format.kind, {
      nonNullable: true,
      validators: [Validators.required],
    });

    const seededLongEdgeShape = seedLongEdgeFromLegacy(dims);
    const seededDimensionsMode: StillsDimensionsMode = resolveSeededMode(dims);

    this.dimensionsModeControl = new FormControl<StillsDimensionsMode>(seededDimensionsMode, {
      nonNullable: true,
      validators: [Validators.required],
    });

    const seededWidth =
      dims.mode === 'explicit' || dims.mode === 'fitWidth' ? dims.width : 1000;
    const seededHeight =
      dims.mode === 'explicit' || dims.mode === 'fitHeight' ? dims.height : 1000;

    this.widthControl = new FormControl<number>(seededWidth, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(1), integerValidator],
    });

    this.heightControl = new FormControl<number>(seededHeight, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(1), integerValidator],
    });

    this.longEdgeControl = new FormControl<number>(seededLongEdgeShape.longEdge, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(1), integerValidator],
    });

    this.shortEdgeControl = new FormControl<number>(seededLongEdgeShape.shortEdge, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(1), integerValidator],
    });

    this.dpiControl = new FormControl<number>(settings.encoding.dpi, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(1)],
    });

    this.jpgQualityControl = new FormControl<number>(format.kind === 'jpg' ? format.quality : 85, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(1), Validators.max(100)],
    });

    this.pngBitDepthControl = new FormControl<number>(
      format.kind === 'png' ? format.bitDepth : 8,
      { nonNullable: true, validators: [Validators.required, Validators.min(1)] },
    );

    this.tiffBitDepthControl = new FormControl<number>(
      format.kind === 'tiff' ? format.bitDepth : 16,
      { nonNullable: true, validators: [Validators.required, Validators.min(1)] },
    );

    this.webpCompressionModeControl = new FormControl<WebPCompressionMode>(
      format.kind === 'webp' ? format.compression.mode : 'lossy',
      { nonNullable: true, validators: [Validators.required] },
    );

    this.webpQualityControl = new FormControl<number>(
      format.kind === 'webp' && format.compression.mode === 'lossy'
        ? format.compression.quality
        : 80,
      {
        nonNullable: true,
        validators: [Validators.required, Validators.min(1), Validators.max(100)],
      },
    );

    this.colorSpaceControl = new FormControl<ColorSpace>(format.colorSpace, {
      nonNullable: true,
      validators: [Validators.required],
    });

    this.subjectCroppingModeControl = new FormControl<StillsSubjectCroppingMode>(crop.mode, {
      nonNullable: true,
      validators: [Validators.required],
    });

    this.whitespacePercentControl = new FormControl<number>(
      crop.mode === 'on' ? crop.whitespacePercent : 10,
      {
        nonNullable: true,
        validators: [Validators.required, Validators.min(1), Validators.max(49), integerValidator],
      },
    );

    this.backgroundColourControl = new FormControl<string>(
      this.sanitizeBackgroundColour(settings.backgroundColour),
      { nonNullable: true, validators: [Validators.required] },
    );
  }

  private sanitizeBackgroundColour(value: string): string {
    if (value.length === 9) return value;
    if (value.length === 7) return `${value}ff`;
    return value;
  }

  get isOther(): boolean {
    return this.reasonControl.value === 'other';
  }

  get isFitWidthDimensions(): boolean {
    return this.dimensionsModeControl.value === 'fitWidth';
  }

  get isFitHeightDimensions(): boolean {
    return this.dimensionsModeControl.value === 'fitHeight';
  }

  get isExplicitDimensions(): boolean {
    return this.dimensionsModeControl.value === 'explicit';
  }

  get isLongEdgeCapDimensions(): boolean {
    return this.dimensionsModeControl.value === 'longEdgeCap';
  }

  get isLongEdgeBoxDimensions(): boolean {
    return this.dimensionsModeControl.value === 'longEdgeBox';
  }

  get showsWidthField(): boolean {
    const mode = this.dimensionsModeControl.value;
    return mode === 'fitWidth' || mode === 'explicit';
  }

  get showsHeightField(): boolean {
    const mode = this.dimensionsModeControl.value;
    return mode === 'fitHeight' || mode === 'explicit';
  }

  get showsLongEdgeField(): boolean {
    const mode = this.dimensionsModeControl.value;
    return mode === 'longEdgeCap' || mode === 'longEdgeBox';
  }

  get showsShortEdgeField(): boolean {
    return this.dimensionsModeControl.value === 'longEdgeBox';
  }

  get isJpg(): boolean {
    return this.formatControl.value === 'jpg';
  }

  get isPng(): boolean {
    return this.formatControl.value === 'png';
  }

  get isTiff(): boolean {
    return this.formatControl.value === 'tiff';
  }

  get isWebp(): boolean {
    return this.formatControl.value === 'webp';
  }

  get isWebpLossy(): boolean {
    return this.isWebp && this.webpCompressionModeControl.value === 'lossy';
  }

  get isSubjectCroppingOn(): boolean {
    return this.subjectCroppingModeControl.value === 'on';
  }

  get showsBackgroundSection(): boolean {
    const kind = this.formatControl.value;
    return kind === 'png' || kind === 'tiff' || kind === 'webp';
  }

  isTransparent(value: string | null): boolean {
    return value?.length === 9 && value.slice(7) === '00';
  }

  colorRgb(value: string | null): string {
    return (value && value.length >= 7 ? value : '#ffffff').slice(0, 7);
  }

  setColor(rgb: string): void {
    const suffix = this.isTransparent(this.backgroundColourControl.value) ? '00' : 'ff';
    this.backgroundColourControl.setValue(`${rgb}${suffix}`);
  }

  setTransparent(transparent: boolean): void {
    const rgb = this.colorRgb(this.backgroundColourControl.value);
    this.backgroundColourControl.setValue(`${rgb}${transparent ? '00' : 'ff'}`);
  }

  get canSubmit(): boolean {
    if (this.submitting) return false;
    if (!this.nameControl.valid) return false;
    if (this.reasonControl.value === null) return false;
    if (this.isOther && !this.detailControl.valid) return false;
    if (!this.formatControl.valid) return false;
    if (!this.dpiControl.valid) return false;
    if (!this.colorSpaceControl.valid) return false;
    if (!this.backgroundColourControl.valid) return false;
    if (!this.isDimensionsValid()) return false;
    if (!this.isFormatSpecificValid()) return false;
    if (!this.isSubjectCroppingValid()) return false;
    return true;
  }

  submit(): void {
    if (!this.canSubmit) return;
    const writeReason = this.buildUpdateReason();
    if (writeReason === null) return;
    this.submitting = true;
    this.service
      .update(this.data.organizationId, this.data.current.id, {
        name: this.nameControl.value.trim(),
        value: { settings: this.buildSettings() },
        writeReason,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: FileSpecification) => this.dialogRef.close(result),
        error: () => {
          this.submitting = false;
        },
      });
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  private buildUpdateReason(): FileSpecificationWriteReason | null {
    const kind = this.reasonControl.value;
    if (kind === null) return null;
    if (kind === 'other') return { kind: 'other', detail: this.detailControl.value.trim() };
    return { kind };
  }

  private isDimensionsValid(): boolean {
    const mode = this.dimensionsModeControl.value;
    if (mode === 'fitWidth') return this.widthControl.valid;
    if (mode === 'fitHeight') return this.heightControl.valid;
    if (mode === 'explicit') return this.widthControl.valid && this.heightControl.valid;
    if (mode === 'longEdgeCap') return this.longEdgeControl.valid;
    return this.longEdgeControl.valid && this.shortEdgeControl.valid;
  }

  private isFormatSpecificValid(): boolean {
    const kind = this.formatControl.value;
    if (kind === 'jpg') return this.jpgQualityControl.valid;
    if (kind === 'png') return this.pngBitDepthControl.valid;
    if (kind === 'tiff') return this.tiffBitDepthControl.valid;
    if (kind === 'webp') {
      if (!this.webpCompressionModeControl.valid) return false;
      if (this.webpCompressionModeControl.value === 'lossy') return this.webpQualityControl.valid;
      return true;
    }
    return true;
  }

  private isSubjectCroppingValid(): boolean {
    if (this.subjectCroppingModeControl.value === 'off') return true;
    return this.whitespacePercentControl.valid;
  }

  private buildSettings(): FileSpecificationStillsSettings {
    return {
      media: 'stills',
      encoding: { format: this.buildFormat(), dpi: this.dpiControl.value },
      dimensions: this.buildDimensions(),
      subjectCropping: this.buildSubjectCropping(),
      backgroundColour: this.backgroundColourControl.value.trim(),
    };
  }

  private buildFormat(): StillsFormatSettings {
    const kind = this.formatControl.value;
    const colorSpace = this.colorSpaceControl.value;
    if (kind === 'jpg') return { kind: 'jpg', quality: this.jpgQualityControl.value, colorSpace };
    if (kind === 'png') return { kind: 'png', bitDepth: this.pngBitDepthControl.value, colorSpace };
    if (kind === 'tiff')
      return { kind: 'tiff', bitDepth: this.tiffBitDepthControl.value, colorSpace };
    if (kind === 'webp')
      return { kind: 'webp', compression: this.buildWebpCompression(), colorSpace };
    return { kind: 'eps', colorSpace };
  }

  private buildWebpCompression(): WebPCompression {
    if (this.webpCompressionModeControl.value === 'lossless') return { mode: 'lossless' };
    return { mode: 'lossy', quality: this.webpQualityControl.value };
  }

  private buildDimensions(): StillsDimensions {
    const mode = this.dimensionsModeControl.value;
    if (mode === 'fitWidth') return { mode: 'fitWidth', width: this.widthControl.value };
    if (mode === 'fitHeight') return { mode: 'fitHeight', height: this.heightControl.value };
    if (mode === 'explicit')
      return { mode: 'explicit', width: this.widthControl.value, height: this.heightControl.value };
    if (mode === 'longEdgeCap')
      return { mode: 'longEdgeCap', longEdge: this.longEdgeControl.value };
    return {
      mode: 'longEdgeBox',
      longEdge: this.longEdgeControl.value,
      shortEdge: this.shortEdgeControl.value,
    };
  }

  private buildSubjectCropping(): StillsSubjectCropping {
    if (this.subjectCroppingModeControl.value === 'off') return { mode: 'off' };
    return { mode: 'on', whitespacePercent: this.whitespacePercentControl.value };
  }
}
